// GameAccountsPlan.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"; // 🆕
import FeedbackPopup from "../popuplessons/FeedbackPopup";
import ExplanationPopup from "../popuplessons/ExplanationPopup";
import LessonProgress from "../progressbar/StatusProgres";
import CustomAlert from "../progressbar/CustomAlert";
import "./GameAccountsPlan.css";

const GameAccountsPlan = () => {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);

  const [selectedOption, setSelectedOption] = useState(null);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanationText, setExplanationText] = useState("");
  const [answerStatus, setAnswerStatus] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { chapterId, lessonId } = location.state || {}; // 🆕 preluare din navigate()



  useEffect(() => {
    const fetchQuestions = async () => {
     try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/accountsplan/${chapterId}/lesson/${lessonId}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.questions && Array.isArray(data.questions)) {
          setQuestions(data.questions);
        } else {
          console.error("Structura întrebărilor este invalidă.", data);
        }
      } catch (err) {
        console.error("Eroare la încărcarea întrebărilor:", err);
      }
    };
    fetchQuestions();
  }, []);

  useEffect(() => {
    const q = questions[current];
    if (!q) return;
    const opts = (q.options || []).map((o, idx) => ({ ...o, __origIndex: idx }));
    setAvailableOptions(opts);
    setSelectedOption(null);
    setIsChecked(false);
    setShowFeedback(false);
    setShowExplanation(false);
    setAnswerStatus(null);
  }, [questions, current]);

  if (!questions.length) return <p>Se încarcă exercițiile...</p>;

  const question = questions[current];
  const progress = Math.round(((current + 1) / questions.length) * 100);

  const restoreOptionToAvailable = (optionObj) => {
    if (!optionObj) return;
    setAvailableOptions(prev => {
      if (prev.some(o => String(o.account) === String(optionObj.account))) return prev;
      const merged = [...prev, optionObj];
      merged.sort((a, b) => (a.__origIndex ?? 0) - (b.__origIndex ?? 0));
      return merged;
    });
  };

  const handleCheck = (optionParam = null) => {
    const opt = optionParam ?? selectedOption;
    if (!opt) return;

    if (String(opt.account) === String(question.correctAccount)) {
      setAnswerStatus("correct");
    } else {
      setAnswerStatus("incorrect");
    }
    setSelectedOption(opt);
    setIsChecked(true);
    setShowFeedback(true);
  };

  const handleNext = () => {
    setCurrent(prev => (prev + 1) % questions.length);
  };

  const handleExitClick = () => setShowAlert(true);
  const handleConfirmExit = () => {
    setShowAlert(false);
    navigate("/LessonsGameAccountsPlan");
  };
  const handleCancelExit = () => setShowAlert(false);

  // DRAG handlers
  const onDragStartOption = (e, account) => {
    e.dataTransfer.setData("text/plain", account);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOverBlank = (e) => e.preventDefault();

  const onDropOnBlank = (e) => {
    e.preventDefault();
    const account = e.dataTransfer.getData("text/plain");
    if (!account) return;
    const opt = availableOptions.find(o => String(o.account) === String(account));
    if (opt) {
      if (selectedOption) restoreOptionToAvailable(selectedOption);
      setAvailableOptions(prev => prev.filter(o => String(o.account) !== String(account)));
      setSelectedOption(opt);
      setIsChecked(false);
      setShowFeedback(false);
      setAnswerStatus(null);
    }
  };

  const onDragStartBlank = (e) => {
    if (!selectedOption) return;
    e.dataTransfer.setData("text/plain", `fromBlank:${selectedOption.account}`);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDropOnOptionsArea = (e) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    if (!raw) return;
    const prefix = "fromBlank:";
    if (raw.startsWith(prefix)) {
      const account = raw.slice(prefix.length);
      if (selectedOption && String(selectedOption.account) === String(account)) {
        restoreOptionToAvailable(selectedOption);
        setSelectedOption(null);
        setIsChecked(false);
        setShowFeedback(false);
        setAnswerStatus(null);
      }
    }
  };

  const onSelectByClick = (account) => {
    if (isChecked) return;
    const opt = availableOptions.find(o => String(o.account) === String(account));
    if (!opt) return;
    if (selectedOption) restoreOptionToAvailable(selectedOption);
    setAvailableOptions(prev => prev.filter(o => String(o.account) !== String(account)));
    setSelectedOption(opt);
    setIsChecked(false);
    setShowFeedback(false);
    setAnswerStatus(null);
  };

  const onBlankClick = () => {
    if (!selectedOption) return;
    restoreOptionToAvailable(selectedOption);
    setSelectedOption(null);
    setIsChecked(false);
    setShowFeedback(false);
    setAnswerStatus(null);
  };

  // RENDER: pentru a păstra pozițiile fixe mapăm peste question.options (ordinea originală).
  const columns = question.options.length || 3;

  return (
    <div className="plan-account-container">
      <div className="plan-account-instruction">
        <LessonProgress percent={progress} onExitClick={handleExitClick} />

        <div className="container-game-plan-account">
          <p className="q-title">Alege contul potrivit!</p>

          <div className="statement-account">
            <div className="blank-row">
              <div
                className={`blank-box ${selectedOption ? "filled" : ""} ${isChecked ? (answerStatus === "correct" ? "correct" : "incorrect") : ""}`}
                onDragOver={onDragOverBlank}
                onDrop={onDropOnBlank}
                role="button"
                aria-label={selectedOption ? `Cont selectat ${selectedOption.account}` : "Loc gol - plasează contul"}
                tabIndex={0}
                draggable={!!selectedOption}
                onDragStart={onDragStartBlank}
                onClick={onBlankClick}
              >
                {selectedOption ? <span className="blank-value">{selectedOption.account}</span> : <span className="blank-placeholder"></span>}
              </div>
            </div>

            <div className="account-text">{question.statement}</div>
          </div>

          {/* GRID: păstrăm pozițiile fixe */}
          <div className="options-plan-account">
            <div
                className="options-row"
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(110px, 1fr))` }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDropOnOptionsArea}
                role="list"
            >
                {question.options.map((orig) => {
                // verificăm dacă orig este disponibil acum
                const isAvailable = availableOptions.some(o => String(o.account) === String(orig.account));
                const isSelected = selectedOption && String(selectedOption.account) === String(orig.account);
                const isCorrect = isChecked && String(orig.account) === String(question.correctAccount);
                const isIncorrect = isChecked && isSelected && String(orig.account) !== String(question.correctAccount);

                if (isAvailable) {
                    // renderăm butonul
                    const opt = availableOptions.find(o => String(o.account) === String(orig.account));
                    return (
                    <button
                        key={orig.account}
                        className={`option-btn ${isSelected ? "selected" : ""} ${isCorrect ? "correct" : ""} ${isIncorrect ? "incorrect" : ""}`}
                        draggable={!isChecked}
                        onDragStart={(e) => onDragStartOption(e, opt.account)}
                        onClick={() => onSelectByClick(opt.account)}
                        disabled={isChecked}
                        aria-label={`Opțiune cont ${opt.account}`}
                    >
                        <div className="opt-number">{opt.account}</div>
                
                    </button>
                    );
                } else {
                    // placeholder (ocupă spațiu, dar e gol)
                    return (
                    <div key={`ph-${orig.account}`} className="option-placeholder" aria-hidden>
                        {/* păstrăm dimensiune și margini */}
                    </div>
                    );
                }
                })}
            </div>
          </div>
        </div>

        <div className="plan-account-check">
          {!isChecked ? (
            <button
              className={`plan-account-verify ${!selectedOption ? "disabled" : ""}`}
              onClick={() => handleCheck()}
              disabled={!selectedOption}
            >
              Verifica
            </button>
          ) : (
            <button className="plan-account-verify" onClick={handleNext}>
              {current + 1 < questions.length ? "Continuă" : "Finalizează"}
            </button>
          )}
        </div>
      </div>

      {showFeedback && (
        <FeedbackPopup
          status={answerStatus}
          onClose={() => setShowFeedback(false)}
          onContinue={handleNext}
          onShowAnswer={() => {
            setShowFeedback(false);
            setExplanationText(question.explanation || "Explicație indisponibilă.");
            setShowExplanation(true);
          }}
        />
      )}

      {showExplanation && (
        <ExplanationPopup explanation={explanationText} onContinue={() => setShowExplanation(false)} />
      )}

      {showAlert && <CustomAlert onConfirm={handleConfirmExit} onCancel={handleCancelExit} />}
    </div>
  );
};

export default GameAccountsPlan;
